import { useEffect, useState } from 'react';

import { apiGet } from 'utils/axios';

export interface RehireMetadataField {
  key: string;
  remoteKey: string | null;
}

export interface RehireMetadata {
  fields: RehireMetadataField[];
  observedValues: Array<{ value: string; count: number }>;
  sampleSize: number;
  syncState: {
    initialSyncCompleted: boolean;
    lastSyncedAt: string | null;
  };
}

const EMPTY_METADATA: RehireMetadata = {
  fields: [],
  observedValues: [],
  sampleSize: 0,
  syncState: { initialSyncCompleted: false, lastSyncedAt: null },
};

interface RehireMetadataResponse {
  data: { metadata: RehireMetadata };
}

async function fetchMetadata(
  connectorUuid: string,
  eligibilityField?: string,
): Promise<RehireMetadata> {
  const params = new URLSearchParams({ connector_uuid: connectorUuid });
  if (eligibilityField) params.set('eligibility_field', eligibilityField);
  const response = (await apiGet(
    `/internal_api/workflow_editor/rehire_check/metadata?${params.toString()}`,
  )) as RehireMetadataResponse;
  return response.data.metadata;
}

export function useRehireMetadata(
  connectorUuid: string,
  eligibilityField: string,
): { metadata: RehireMetadata; loading: boolean } {
  const [metadata, setMetadata] = useState<RehireMetadata>(EMPTY_METADATA);
  const [loading, setLoading] = useState(Boolean(connectorUuid));

  useEffect(() => {
    let mounted = true;
    if (!connectorUuid) {
      setMetadata(EMPTY_METADATA);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    void fetchMetadata(connectorUuid)
      .then(async baseMetadata => {
        const field =
          eligibilityField ||
          (baseMetadata.fields.length === 1 ? baseMetadata.fields[0].key : '');
        return field ? fetchMetadata(connectorUuid, field) : baseMetadata;
      })
      .then(result => {
        if (mounted) setMetadata(result);
      })
      .catch(() => {
        if (mounted) setMetadata(EMPTY_METADATA);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [connectorUuid, eligibilityField]);

  return { metadata, loading };
}
